import { checkStatus } from '../../utilities/fetch';
import { createQueryString } from '../../utilities/utilities';
import {
  checkEmailPath,
  registrationRequestsPath,
  organizationsPath,
} from '../../utilities/api_relative_paths';
import pickBy from 'lodash/pickBy';

/**
 * GET: verifies if user email is valid or not
 *
 * @param {string} email - Email to verify
 * @returns {null|Promise} Promise that can be consumed or null
 */
const checkUserEmail = (email) => {
  if (!email) return null;

  const params = createQueryString({
    value: email,
    field: 'consultant[login]',
  });

  const path = checkEmailPath + params;

  return fetch(path)
    .then(checkStatus)
    .then((response) => response.json());
};

/**
 * POST: does a new user registration request
 *
 * @param {object} registrationRequest - data to request registration
 * @param {string} registrationRequest.email - user email request registration
 * @param {string} registrationRequest.packageShortName - package short_name request registration
 * @param {string} registrationRequest.paymentPage - payment page to load on payment step
 * @param {string} registrationRequest.returnTo - page to return to
 * @returns {null|Promise} Promise that can be consumed or null
 */
const registerUserRequest = ({
  email,
  packageShortName: package_short_name,
  paymentPage: permalink_payment_page,
  returnTo: return_to,
}) => {
  if (!email || !package_short_name) return null;

  const userData = pickBy({
    email,
    package_short_name,
    permalink_payment_page,
    return_to,
  });

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userData),
  };

  return fetch(registrationRequestsPath, options)
    .then(checkStatus)
    .then((response) => response.json());
};

/**
 * POST: does a new user organization registration request
 *
 * @param {number} organizationId - organization id
 * @param {number} signupPageId - signup page id
 * @param {object} registrationRequest - data to request registration
 * @param {string} registrationRequest.email - user email request registration
 * @param {string} registrationRequest.packageShortName - package short_name request registration
 * @returns {null|Promise} Promise that can be consumed or null
 */
const registerUserOrganizationRequest = (organizationId, signupPageId, registrationRequest) => {
  if (!registrationRequest) return null;

  const { email, packageShortName: package_short_name } = registrationRequest;
  if (!organizationId || !signupPageId || !email || !package_short_name) return null;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, package_short_name, org_signup_page_id: signupPageId }),
  };

  return fetch(`${organizationsPath}/${organizationId}/signup_pages/${signupPageId}`, options)
    .then(checkStatus)
    .then((response) => response.json());
};

/**
 * @param {number} organizationId ID of the organization
 * @param {number} signupPageId ID of the signup page
 * @returns {Promise} Promise that can be extended
 */
const upgradeExistingCmOrganizationRequest = (organizationId, signupPageId) => {
  if (!organizationId || !signupPageId) return null;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  return fetch(`${organizationsPath}/${organizationId}/signup_pages/${signupPageId}`, options)
    .then(checkStatus)
    .catch((error) => Promise.reject(error));
};

/**
 * POST: creates a new user
 *
 * @param {object} userData - user data to register
 * @param {string} authToken - CSRF auth token
 * @returns {null|Promise} Promise that can be consumed or null
 */
const registerUser = (userData, authToken) => {
  if (!authToken) return null;

  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': authToken,
    },
    body: JSON.stringify(userData),
  };

  const url = '/register';

  return fetch(url, options)
    .then(checkStatus)
    .then((response) => response.json())
    .catch((error) => {
      return Promise.reject(error);
    });
};

export {
  checkUserEmail,
  registerUserRequest,
  registerUserOrganizationRequest,
  registerUser,
  upgradeExistingCmOrganizationRequest,
};
